import { useCallback, useEffect, useState } from 'react';
import { Info } from 'shared/icons';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import Button from '../ui/Button';
import ErrorCard from '../ui/ErrorCard';
import Input from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { useToast } from '../ui/Toast';
import { DASH, FormatDay, ParamsEditor } from './pricingShared';

/**
 * PARÂMETROS — constantes fiscais da empresa (PIS/COFINS, crédito de ICMS
 * sobre frete, flags a confirmar com a contabilidade, honorário e fallback
 * do ST, premissas do simulador) com VIGÊNCIA: salvar com uma data nova cria
 * uma vigência nova; a mesma data atualiza a linha. Cada lote é calculado
 * com a vigência da data de emissão da nota.
 */

const CompanyParamsPanel = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canWrite = checkPermission('pricing:write');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({ valid_from: '', valid_to: '', note: '', params: {} });
    const [saving, setSaving] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);

    const refresh = useCallback(() => setRefreshTick((tick) => tick + 1), []);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        api.getPricingParams()
            .then((payload) => {
                if (cancelled) {
                    return;
                }
                setData(payload);
                const current = payload?.current;
                setForm({
                    valid_from: current?.valid_from || new Date().toISOString().slice(0, 10),
                    valid_to: current?.valid_to || '',
                    note: current?.note || '',
                    // Prefill com o EFETIVO (defaults ← vigente): a tela mostra o
                    // número que está valendo, não só o que foi digitado.
                    params: { ...(payload?.effective || {}) },
                });
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar os parâmetros.');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [refreshTick]);

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            await api.savePricingParams({
                valid_from: form.valid_from,
                valid_to: form.valid_to || null,
                note: form.note.trim() || null,
                params: form.params,
            });
            toast.success('Parâmetros salvos');
            refresh();
        } catch (err) {
            toast.error(err?.message || 'Não foi possível salvar');
        } finally {
            setSaving(false);
        }
    };

    if (error) {
        return (
            <ErrorCard
                title="Erro ao carregar parâmetros"
                message={error}
                action="Tentar novamente"
                actionFn={refresh}
            />
        );
    }
    if (!data) {
        return <LoadingSpinner className="py-12" />;
    }

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-xl border border-info-200 bg-info-50 p-4 dark:border-info-800 dark:bg-info-900/20">
                <Info size={20} className="mt-0.5 shrink-0 text-info-600 dark:text-info-400" />
                <div className="text-sm text-info-800 dark:text-info-200">
                    <p>
                        Constantes do modelo v5 (Lucro Real, sede SP). <strong>Não altere sem a contabilidade</strong>:
                        foram validadas contra a planilha histórica. Mudança de constante entra como{' '}
                        <strong>vigência nova</strong> — os lotes antigos continuam calculados com a vigência da data da
                        nota.
                    </p>
                    <p className="mt-1 text-xs">
                        Vigência atual:{' '}
                        {data.current
                            ? `${FormatDay(data.current.valid_from)} → ${data.current.valid_to ? FormatDay(data.current.valid_to) : 'aberta'}`
                            : 'nenhuma salva (usando os defaults do código)'}
                    </p>
                </div>
            </div>

            <form
                onSubmit={submit}
                className="space-y-4 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950"
            >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        label="Vigente a partir de"
                        type="date"
                        value={form.valid_from}
                        onChange={(event) => setForm((prev) => ({ ...prev, valid_from: event.target.value }))}
                        disabled={saving || !canWrite}
                    />
                    <Input
                        label="Vigente até (opcional)"
                        type="date"
                        value={form.valid_to}
                        onChange={(event) => setForm((prev) => ({ ...prev, valid_to: event.target.value }))}
                        disabled={saving || !canWrite}
                    />
                    <Input
                        label="Observação"
                        value={form.note}
                        onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))}
                        disabled={saving || !canWrite}
                        placeholder="ex.: fechado com a contabilidade em 08/09/2026"
                    />
                </div>
                <fieldset disabled={!canWrite} className="disabled:opacity-70">
                    <ParamsEditor
                        value={form.params}
                        onChange={(params) => setForm((prev) => ({ ...prev, params }))}
                        mode="company"
                    />
                </fieldset>
                {canWrite ? (
                    <div className="flex justify-end">
                        <Button type="submit" loading={saving} disabled={saving || !form.valid_from}>
                            Salvar vigência
                        </Button>
                    </div>
                ) : (
                    <p className="text-xs text-mono-500">Somente leitura — edição exige a permissão pricing:write.</p>
                )}
            </form>

            {data.history?.length ? (
                <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <p className="mb-2 text-sm font-medium text-black dark:text-white">Vigências salvas</p>
                    <ul className="space-y-1 text-xs text-mono-600 dark:text-mono-300">
                        {data.history.map((entry) => (
                            <li key={entry.id} className="flex flex-wrap gap-2">
                                <span className="font-mono">
                                    {FormatDay(entry.valid_from)} →{' '}
                                    {entry.valid_to ? FormatDay(entry.valid_to) : 'aberta'}
                                </span>
                                <span>{entry.note || DASH}</span>
                                <span className="text-mono-400">
                                    {Object.keys(entry.params || {}).length} parâmetro(s)
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

export default CompanyParamsPanel;
